import {DatePickerMode, DatePickerValue, SplitModeRole} from "@/components/datePicker/DatePicker.types.ts";
import styles from "@/components/datePicker/subComponents/DatePickerCalendar/DatePickerCalendar.module.scss";
import {DatePickerTimeSelectList} from "@/components/datePicker/subComponents/DatePickerTimeSelectList/DatePickerTimeSelectList.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {useMediaQuery} from "@/lib/hooks/useMediaQuery.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {DateRange} from "react-day-picker";

type DatePickerTimeSelectBaseProps = {
  name?: string;
  timezone: string;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  timeIntervalMinutes: number;
  splitRole?: SplitModeRole;
};

type DatePickerTimeSelectProps<Mode extends DatePickerMode> = DatePickerTimeSelectBaseProps & {
  mode: Mode;
  selected?: DatePickerValue<Mode>;
  setSelectedAndOnChange: (value: DatePickerValue<Mode>, fixedRange?: StandardDateRange) => void;
};

const getDateForSplitRole = (selected: DateRange, splitRole?: SplitModeRole): Date | undefined => {
  if (splitRole === "start") {
    return selected?.from || selected?.to; // if from is not set, use to so start date won't be after end date, else undefined (current date)
  } else {
    return selected?.to || selected?.from; // if to is not set, use from so end date won't be before start date, else undefined (current date)
  }
};

export const DatePickerTimeSelect = <Mode extends DatePickerMode = "range">({
  mode,
  disableFutureDates,
  disablePastDates,
  timezone,
  selected,
  timeIntervalMinutes,
  splitRole,
  setSelectedAndOnChange,
}: DatePickerTimeSelectProps<Mode>) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  return (
    <>
      <Divider orientation={isMobile ? "horizontal" : "vertical"} className={styles.datePickerDividerTime} />
      <div className={styles.datePickerTimeSelectList}>
        {mode === "split" && (
          <DatePickerTimeSelectList
            onSelect={(date: Date) => {
              const selectedRange = selected as DateRange | undefined;
              if (splitRole === "start") {
                if (selectedRange?.to && date > selectedRange.to) {
                  setSelectedAndOnChange({from: date, to: undefined} as DatePickerValue<Mode>);
                  return;
                }
                setSelectedAndOnChange({from: date, to: selectedRange?.to} as DatePickerValue<Mode>);
              } else {
                if (selectedRange?.from && date < selectedRange.from) {
                  setSelectedAndOnChange({from: undefined, to: date} as DatePickerValue<Mode>);
                  return;
                }
                setSelectedAndOnChange({
                  from: selectedRange?.from,
                  to: date,
                } as DatePickerValue<Mode>);
              }
            }}
            disabledAfter={disableFutureDates ? new Date() : undefined}
            disabledBefore={disablePastDates ? new Date() : undefined}
            intervalMinutes={timeIntervalMinutes}
            timezone={timezone}
            date={getDateForSplitRole(selected as DateRange, splitRole)}
          />
        )}
        {mode === "single" && (
          <DatePickerTimeSelectList
            onSelect={(date: Date) => {
              setSelectedAndOnChange(date as DatePickerValue<Mode>);
            }}
            disabledAfter={disableFutureDates ? new Date() : undefined}
            disabledBefore={disablePastDates ? new Date() : undefined}
            intervalMinutes={timeIntervalMinutes}
            timezone={timezone}
            date={selected as Date | undefined}
          />
        )}
      </div>
    </>
  );
};
